import apiService from "./apiService";

/**
 * "Sign in as" another staff user of this hospital, for support. The backend
 * decides who may do it (super admins, holders of an impersonating role) and
 * logs every session. The admin's own session is kept aside and restored on
 * Stop. An impersonation token lasts one hour; after that the session ends
 * with a sign-out rather than quietly turning back into the admin.
 */
const KEY = "auth_impersonation";

export interface ImpersonationState {
  original_token: string;
  original_refresh_token: string;
  original_user: any;
  target: { id: number; firstname?: string; lastname?: string; username?: string; email?: string };
  started_at: string;
}

export function currentImpersonation(): ImpersonationState | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as ImpersonationState) : null;
  } catch {
    return null;
  }
}

export async function startImpersonation(targetUserId: number, reason: string): Promise<void> {
  const res = await apiService.post("/impersonation/start", { targetUserId, reason });
  const originalUser = JSON.parse(localStorage.getItem("auth_user") || "{}");
  const state: ImpersonationState = {
    original_token: localStorage.getItem("auth_token") || "",
    original_refresh_token: localStorage.getItem("auth_refresh_token") || "",
    original_user: originalUser,
    target: res.targetUser,
    started_at: new Date().toISOString(),
  };
  localStorage.setItem(KEY, JSON.stringify(state));
  localStorage.setItem("auth_token", res.impersonationToken);
  // No refresh while impersonating: when the hour is up the session ends.
  localStorage.setItem("auth_refresh_token", "impersonation-session");
  localStorage.setItem("auth_user", JSON.stringify({
    ...res.targetUser,
    is_super_admin: false,
    tenant_id: originalUser.tenant_id,
    hospital_name: originalUser.hospital_name,
    roles: [],
  }));
  window.location.assign("/dashboard");
}

export async function stopImpersonation(): Promise<void> {
  const state = currentImpersonation();
  if (!state) return;
  let token = state.original_token;
  try {
    const res = await apiService.post("/impersonation/stop");
    token = res?.originalToken || token;
  } catch {
    // Expired or already ended: fall back to the admin's own saved session.
  }
  localStorage.setItem("auth_token", token);
  localStorage.setItem("auth_refresh_token", state.original_refresh_token);
  localStorage.setItem("auth_user", JSON.stringify(state.original_user));
  localStorage.removeItem(KEY);
  window.location.assign("/admin/users");
}
